// frontend/src/screens/admin/WardPreShiftListScreen.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { lightGreen, red, orange, blue, grey } from '@mui/material/colors';
import { useLayout } from '../../context/LayoutContext';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';

import {
  useGetPreShiftsQuery,
  useApprovePreShiftMutation,
  useRejectPreShiftMutation,
  useDeletePreShiftMutation,
} from '../../slices/preShiftApiSlice';

import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice';

// Styled DataGrid with conditional row styles
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.pending-row {
    background-color: ${orange[50]};
  }
  & .${gridClasses.row}.approved-row {
    background-color: ${lightGreen[100]};
  }
  & .${gridClasses.row}.rejected-row {
    background-color: ${red[100]};
  }
`;

// Scrollable container styles
const scrollableTableStyle = {
  maxHeight: '70vh',
  overflowY: 'auto',
  border: '1px solid #ddd',
  borderRadius: '4px',
  position: 'relative',
  '&::-webkit-scrollbar': {
    width: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '5px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
};

const fixedHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Helper function to extract MongoDB OID
const extractMongoId = (id) => {
  if (!id) return null;
  if (typeof id === 'object' && id.$oid) return id.$oid;
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id._id) return extractMongoId(id._id);
  return id?.toString() || null;
};

const WardPreShiftListScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { setFluid } = useLayout();

  // Get the manager's assigned directorate from userInfo
  const managerDirectorate = userInfo?.directorateName || '';

  const [filters, setFilters] = useState({
    directorate: managerDirectorate, // Auto-set to manager's directorate
    wardDepartmentArea: '',
    categoryOfStaff: '',
    preShiftStatus: '',
    dateRange: {
      start: '',
      end: '',
    },
  });

  // State for modals
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedPreShift, setSelectedPreShift] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  // API hooks
  const { data: preShifts, isLoading, isError, error, refetch } = useGetPreShiftsQuery();
  const { data: hospitalLocations, isLoading: loadingLocations } = useGetHospitalLocationsQuery();
  
  const [approvePreShift, { isLoading: approving }] = useApprovePreShiftMutation();
  const [rejectPreShift, { isLoading: rejecting }] = useRejectPreShiftMutation();
  const [deletePreShift, { isLoading: deleting }] = useDeletePreShiftMutation();

  // Create a map of hospital location IDs to their data
  const hospitalLocationMap = useMemo(() => {
    if (!hospitalLocations) return {};
    const map = {};
    hospitalLocations.forEach(location => {
      const locationId = extractMongoId(location._id);
      if (locationId) {
        map[locationId] = {
          directorate: location.directorate,
          department: location.department,
          wardDepartmentArea: location.wardDepartmentArea,
          costCentreCode: location.costCentreCode,
        };
      }
    });
    return map;
  }, [hospitalLocations]);

  // Enrich pre-shifts with location data
  const enrichedPreShifts = useMemo(() => {
    if (!preShifts || !hospitalLocations) return [];
    
    return preShifts.map(preShift => {
      const locationId = extractMongoId(preShift.hospitalLocationId);
      const locationData = locationId ? hospitalLocationMap[locationId] : null;
      
      return {
        ...preShift,
        directorate: locationData?.directorate || preShift.directorate || 'N/A',
        department: locationData?.department || 'N/A',
        displayWardDepartmentArea: preShift.wardDepartmentArea || locationData?.wardDepartmentArea || 'N/A',
        requestedDate: new Date(preShift.createdAt).toLocaleDateString(),
        shiftDate: new Date(preShift.startDateTime).toLocaleDateString(),
        shiftTime: `${new Date(preShift.startDateTime).toLocaleTimeString()} - ${new Date(preShift.endDateTime).toLocaleTimeString()}`,
      };
    });
  }, [preShifts, hospitalLocations, hospitalLocationMap]);

  // Filter pre-shifts to only show those for the manager's directorate
  const managerPreShifts = useMemo(() => {
    if (!enrichedPreShifts || !managerDirectorate) return [];
    
    return enrichedPreShifts.filter(preShift => 
      preShift.directorate === managerDirectorate
    );
  }, [enrichedPreShifts, managerDirectorate]);

  // Extract unique filter options (only from manager's pre-shifts)
  const filterOptions = useMemo(() => {
    if (!managerPreShifts || managerPreShifts.length === 0) {
      return { directorates: [], wards: [], categories: [], statuses: [] };
    }

    return {
      directorates: [managerDirectorate], // Only show the manager's directorate
      wards: [...new Set(
        managerPreShifts
          .map(shift => shift.displayWardDepartmentArea)
          .filter(ward => ward && ward !== 'N/A')
      )].sort(),
      categories: [...new Set(
        managerPreShifts
          .map(shift => shift.categoryOfStaff)
          .filter(cat => cat)
      )].sort(),
      statuses: [...new Set(
        managerPreShifts
          .map(shift => shift.preShiftStatus)
          .filter(status => status)
      )].sort(),
    };
  }, [managerPreShifts, managerDirectorate]);

  // Filter pre-shifts (using the manager-filtered list as base)
  const filteredPreShifts = useMemo(() => {
    if (!managerPreShifts) return [];

    return managerPreShifts.filter(preShift => {
      // Note: directorate filter is removed since we're already filtered by manager's directorate
      if (filters.wardDepartmentArea && preShift.displayWardDepartmentArea !== filters.wardDepartmentArea) return false;
      if (filters.categoryOfStaff && preShift.categoryOfStaff !== filters.categoryOfStaff) return false;
      if (filters.preShiftStatus && preShift.preShiftStatus !== filters.preShiftStatus) return false;
      
      if (filters.dateRange.start) {
        const startDate = new Date(filters.dateRange.start);
        const shiftStartDate = new Date(preShift.startDateTime);
        if (shiftStartDate < startDate) return false;
      }
      
      if (filters.dateRange.end) {
        const endDate = new Date(filters.dateRange.end);
        const shiftEndDate = new Date(preShift.endDateTime);
        if (shiftEndDate > endDate) return false;
      }
      
      return true;
    });
  }, [managerPreShifts, filters]);

  const isFilterActive = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'dateRange') return value.start || value.end;
      // Don't count directorate as active since it's auto-set
      if (key === 'directorate') return false;
      return Boolean(value);
    });
  }, [filters]);

  const clearFilters = () => {
    setFilters({
      directorate: managerDirectorate, // Keep the manager's directorate
      wardDepartmentArea: '',
      categoryOfStaff: '',
      preShiftStatus: '',
      dateRange: { start: '', end: '' },
    });
  };

  const handleFilterChange = (filterName, value) => {
    if (filterName.includes('dateRange')) {
      const [range, type] = filterName.split('.');
      setFilters(prev => ({
        ...prev,
        dateRange: {
          ...prev.dateRange,
          [type]: value,
        },
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterName]: value,
      }));
    }
  };

  // Column definitions
  const columns = [
    {
      field: 'requestedDate',
      headerName: 'Requested',
      width: 100,
    },
    {
      field: 'shiftDate',
      headerName: 'Shift Date',
      width: 100,
    },
    {
      field: 'shiftTime',
      headerName: 'Shift Time',
      width: 180,
    },
    { 
      field: 'displayWardDepartmentArea', 
      headerName: 'Ward/Dept Area', 
      width: 150,
    },
    {
      field: 'directorate',
      headerName: 'Directorate',
      width: 150,
    },
    { 
      field: 'categoryOfStaff', 
      headerName: 'Staff Category', 
      width: 120 
    },
    { 
      field: 'reasonForRequest', 
      headerName: 'Reason', 
      width: 120 
    },
    { 
      field: 'preShiftStatus', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => {
        const status = params.value;
        let color = 'default';
        let icon = null;
        
        switch(status) {
          case 'Pending':
            color = 'warning';
            icon = <HistoryIcon fontSize="small" />;
            break;
          case 'Approved':
            color = 'success';
            icon = <CheckCircleIcon fontSize="small" />;
            break;
          case 'Rejected':
            color = 'error';
            icon = <CancelIcon fontSize="small" />;
            break;
          default:
            color = 'default';
        }
        
        return (
          <Chip 
            label={status} 
            color={color} 
            size="small"
            icon={icon}
          />
        );
      }
    },
    {
      field: 'requestedByName',
      headerName: 'Requested By',
      width: 120,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPreShift(params.row);
                setOpenViewModal(true);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {params.row.preShiftStatus === 'Pending' && (
            <>
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPreShift(params.row);
                    setOpenApproveModal(true);
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPreShift(params.row);
                    setOpenRejectModal(true);
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPreShift(params.row);
                    setOpenDeleteModal(true);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          
          {params.row.preShiftStatus === 'Approved' && params.row.createdShiftId && (
            <Tooltip title="View Created Shift">
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/shifts/${params.row.createdShiftId}/edit`);
                }}
              >
                View Shift
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Row styling function
  const getRowClassName = (params) => {
    switch (params.row.preShiftStatus) {
      case 'Pending':
        return 'pending-row';
      case 'Approved':
        return 'approved-row';
      case 'Rejected':
        return 'rejected-row';
      default:
        return '';
    }
  };

  // Handle approve
  const handleApprove = async () => {
    try {
      await approvePreShift({
        id: selectedPreShift._id,
        approvalNotes,
      }).unwrap();
      
      toast.success('Pre-shift approved and shift created successfully!');
      setOpenApproveModal(false);
      setSelectedPreShift(null);
      setApprovalNotes('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to approve pre-shift');
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await rejectPreShift({
        id: selectedPreShift._id,
        rejectionReason,
      }).unwrap();
      
      toast.success('Pre-shift rejected successfully!');
      setOpenRejectModal(false);
      setSelectedPreShift(null);
      setRejectionReason('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to reject pre-shift');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deletePreShift(selectedPreShift._id).unwrap();
      toast.success('Pre-shift deleted successfully!');
      setOpenDeleteModal(false);
      setSelectedPreShift(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete pre-shift');
    }
  };

  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  if (isLoading || loadingLocations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', width: '100%', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/manager/ward-preshifts/create')}
        >
          Create Pre-Shift Request
        </Button>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Toggle Filters">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters {isFilterActive && (
                <Chip 
                  size="small" 
                  label="Active" 
                  color="primary" 
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                />
              )}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter Panel */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" disabled>
                <InputLabel>Directorate</InputLabel>
                <Select
                  value={filters.directorate}
                  label="Directorate"
                  onChange={(e) => handleFilterChange('directorate', e.target.value)}
                >
                  <MenuItem value={managerDirectorate}>{managerDirectorate}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ward/Dept Area</InputLabel>
                <Select
                  value={filters.wardDepartmentArea}
                  label="Ward/Dept Area"
                  onChange={(e) => handleFilterChange('wardDepartmentArea', e.target.value)}
                >
                  <MenuItem value="">All Areas</MenuItem>
                  {filterOptions.wards.map(ward => (
                    <MenuItem key={ward} value={ward}>{ward}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Staff Category</InputLabel>
                <Select
                  value={filters.categoryOfStaff}
                  label="Staff Category"
                  onChange={(e) => handleFilterChange('categoryOfStaff', e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {filterOptions.categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.preShiftStatus}
                  label="Status"
                  onChange={(e) => handleFilterChange('preShiftStatus', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {filterOptions.statuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={filters.dateRange.start}
                onChange={(e) => handleFilterChange('dateRange.start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={filters.dateRange.end}
                onChange={(e) => handleFilterChange('dateRange.end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={!isFilterActive}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setShowFilters(false)}
                >
                  Apply
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Directorate Info Banner */}
      <Paper sx={{ p: 1, mb: 2, bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="body2">
          Showing pre-shift requests for: <strong>{managerDirectorate}</strong>
        </Typography>
      </Paper>

      {/* Results Summary */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" color="textSecondary">
          Showing {filteredPreShifts.length} of {managerPreShifts?.length || 0} pre-shift requests for your directorate
        </Typography>
        
        {isFilterActive && (
          <Chip 
            label={`${filteredPreShifts.length} filtered`}
            color="primary"
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* Main Content */}
      <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardHeader 
          title="Pre-Shift Requests" 
          subheader={`Review and approve shift requests for ${managerDirectorate}`}
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            borderBottom: '1px solid #ddd',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        />
        
        {/* Fixed Header Row */}
        <Box sx={fixedHeaderStyle}>
          <Grid container sx={{ bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
            {columns.map((column) => (
              <Grid item key={column.field} sx={{ 
                width: column.width, 
                minWidth: column.width,
                p: 1,
                borderRight: 1,
                borderColor: 'divider',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
              }}>
                {column.headerName}
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Scrollable Data Grid */}
        <Box sx={scrollableTableStyle}>
          <StyledDataGrid
            density="compact"
            rows={filteredPreShifts}
            columns={columns}
            getRowId={(row) => extractMongoId(row._id) || row._id}
            getRowClassName={getRowClassName}
            hideFooter={true}
            disableSelectionOnClick
            disableColumnMenu
            disableDensitySelector
            onRowClick={(params) => {
              setSelectedPreShift(params.row);
              setOpenViewModal(true);
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                cursor: 'pointer',
              },
            }}
          />
          
          {filteredPreShifts.length === 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 200,
              color: 'text.secondary',
              flexDirection: 'column',
              gap: 1,
            }}>
              <Typography variant="h6">No pre-shift requests found</Typography>
              <Typography variant="body2">
                {isFilterActive 
                  ? "No requests match your current filters. Try adjusting or clearing filters."
                  : `No pre-shift requests available for ${managerDirectorate}. Create a new request to get started.`}
              </Typography>
              {isFilterActive && (
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          )}
        </Box>
        
        <Box sx={{ 
          p: 1, 
          borderTop: 1, 
          borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant="caption" color="textSecondary">
            Total: {filteredPreShifts.length} requests for {managerDirectorate}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Click any row to view details
          </Typography>
        </Box>
      </Card>

      {/* View Details Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Pre-Shift Request Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenViewModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Request Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Chip 
                      label={selectedPreShift.preShiftStatus}
                      color={selectedPreShift.preShiftStatus === 'Pending' ? 'warning' : 
                             selectedPreShift.preShiftStatus === 'Approved' ? 'success' : 'error'}
                      icon={selectedPreShift.preShiftStatus === 'Pending' ? <HistoryIcon /> :
                            selectedPreShift.preShiftStatus === 'Approved' ? <CheckCircleIcon /> : <CancelIcon />}
                    />
                    <Typography variant="body2">
                      Requested on: {new Date(selectedPreShift.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="primary">Shift Details</Typography>
                <Typography><strong>Start:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()}</Typography>
                <Typography><strong>End:</strong> {new Date(selectedPreShift.endDateTime).toLocaleString()}</Typography>
                <Typography><strong>Category:</strong> {selectedPreShift.categoryOfStaff}</Typography>
                <Typography><strong>Reason:</strong> {selectedPreShift.reasonForRequest}</Typography>
                <Typography><strong>Details:</strong> {selectedPreShift.reasonForRequestDetails}</Typography>
                {selectedPreShift.specialRequirements && (
                  <Typography><strong>Special Req:</strong> {selectedPreShift.specialRequirements}</Typography>
                )}
                {selectedPreShift.comments && (
                  <Typography><strong>Comments:</strong> {selectedPreShift.comments}</Typography>
                )}
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="primary">Location Details</Typography>
                <Typography><strong>Directorate:</strong> {selectedPreShift.directorate}</Typography>
                <Typography><strong>Ward/Dept:</strong> {selectedPreShift.displayWardDepartmentArea}</Typography>
                <Typography><strong>Department:</strong> {selectedPreShift.department}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="primary">Requestor Details</Typography>
                <Typography><strong>Name:</strong> {selectedPreShift.requestedByName}</Typography>
                <Typography><strong>Email:</strong> {selectedPreShift.requestedByEmail}</Typography>
              </Grid>

              {selectedPreShift.preShiftStatus !== 'Pending' && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="primary">Approval Details</Typography>
                  <Typography><strong>Approved By:</strong> {selectedPreShift.approvedByName || 'N/A'}</Typography>
                  <Typography><strong>Approved At:</strong> {selectedPreShift.approvedAt ? new Date(selectedPreShift.approvedAt).toLocaleString() : 'N/A'}</Typography>
                  {selectedPreShift.rejectionReason && (
                    <Typography><strong>Rejection Reason:</strong> {selectedPreShift.rejectionReason}</Typography>
                  )}
                  {selectedPreShift.approvalNotes && (
                    <Typography><strong>Approval Notes:</strong> {selectedPreShift.approvalNotes}</Typography>
                  )}
                </Grid>
              )}

              {selectedPreShift.preShiftStatus === 'Approved' && selectedPreShift.createdShiftId && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <AlertTitle>Shift Created</AlertTitle>
                    This request has been approved and a shift has been created.
                    <Button 
                      size="small" 
                      sx={{ ml: 2 }}
                      variant="outlined"
                      onClick={() => {
                        setOpenViewModal(false);
                        navigate(`/admin/shifts/${selectedPreShift.createdShiftId}/edit`);
                      }}
                    >
                      View Created Shift
                    </Button>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
          {selectedPreShift?.preShiftStatus === 'Pending' && (
            <>
              <Button 
                variant="contained" 
                color="success"
                onClick={() => {
                  setOpenViewModal(false);
                  setOpenApproveModal(true);
                }}
              >
                Approve
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={() => {
                  setOpenViewModal(false);
                  setOpenRejectModal(true);
                }}
              >
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={openApproveModal} onClose={() => setOpenApproveModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Approve Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenApproveModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Confirm Approval</AlertTitle>
                This action will create an actual shift from this request.
              </Alert>
              
              <Typography variant="body2" gutterBottom>
                <strong>Shift:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()} - {new Date(selectedPreShift.endDateTime).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Ward:</strong> {selectedPreShift.displayWardDepartmentArea}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Staff Category:</strong> {selectedPreShift.categoryOfStaff}
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                margin="normal"
                label="Approval Notes (Optional)"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes about this approval..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenApproveModal(false);
            setApprovalNotes('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleApprove}
            disabled={approving}
          >
            {approving ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={openRejectModal} onClose={() => setOpenRejectModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reject Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenRejectModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Confirm Rejection</AlertTitle>
                This request will be marked as rejected and no shift will be created.
              </Alert>
              
              <Typography variant="body2" gutterBottom>
                <strong>Shift:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()} - {new Date(selectedPreShift.endDateTime).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Ward:</strong> {selectedPreShift.displayWardDepartmentArea}
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                margin="normal"
                label="Rejection Reason *"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                error={!rejectionReason.trim()}
                helperText={!rejectionReason.trim() ? 'Rejection reason is required' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenRejectModal(false);
            setRejectionReason('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleReject}
            disabled={rejecting || !rejectionReason.trim()}
          >
            {rejecting ? <CircularProgress size={24} /> : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Delete Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenDeleteModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error">
            <AlertTitle>Warning</AlertTitle>
            Are you sure you want to delete this pre-shift request? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WardPreShiftListScreen;